import type { AssetRef, CreditEstimate, EditKind, PipelineNode, StudioStep } from "@loumy/contracts";
import { EDIT_KIND_CAPABILITIES } from "@loumy/contracts";
import { price } from "@loumy/pricing";
import type { ZodError, ZodIssue } from "zod";
import { getCore } from "../registry/toolRegistry";
import { makePreviewContext, PREVIEW_PRICE_BOOK } from "../registry/previewContext";

// Live-Credits-Vorschau (§4): build → price im Browser, mit denselben reinen Engines
// wie der Server. Best-effort + fail-soft: ein Bauen-/Preis-Fehler (z.B. ungültige/
// unvollständige Settings) gibt `null` zurück → UI zeigt „—" statt zu crashen. Der
// verbindliche Estimate/Hold entsteht ausschließlich serverseitig im Submit (D34).

export function estimateCredits(toolId: string, settings: unknown, inputs: AssetRef[] = []): CreditEstimate | null {
  try {
    const core = getCore(toolId);
    if (!core) return null;
    const ctx = makePreviewContext(new Date().toISOString());
    // D101: rohe Settings mitgeben — modell-gebundene Bounds wie im Server-Submit.
    const schema = core.boundSettingsSchema ? core.boundSettingsSchema(ctx, settings) : core.settingsSchema;
    const parsed = schema.parse(settings);
    const spec = core.buildPipeline(parsed, inputs, ctx);
    return price({ toolId, spec }, PREVIEW_PRICE_BOOK);
  } catch {
    return null;
  }
}

/** editKind → Preis-toolId — SPIEGEL von EDIT_PRICE_TOOL_ID (worker-shared edits.ts, D124/D130/D139). */
const EDIT_PRICE_TOOL_ID: Record<EditKind, string> = {
  "image-key": "chroma-key-remover",
  "spritesheet-rekey": "spritesheet-rekey",
  "spritesheet-loopfix": "spritesheet-loopfix",
  studio: "studio", // D139: kein toolBaseFee ⇒ Basis 0; Preis = Σ flat-Steps
};

/**
 * Browser-Edit-Preis-VORSCHAU (§9.17/D124/D130): derselbe synthetische Plan wie der Server
 * (`editPricePlan` in worker-shared) — Capability-Knoten aus EDIT_KIND_CAPABILITIES
 * (SSOT, contracts), `image-key` unter `chroma-key-remover` (inkl. Basisgebühr = exakt
 * der Job-Preis), die Sheet-Edits unter eigener Id OHNE `spritesheet`-Basis.
 * NICHT autoritativ — der verbindliche Charge entsteht serverseitig beim Speichern.
 */
export function estimateEditCredits(editKind: EditKind): number | null {
  // D139: studio rechnet step-getrieben (estimateStudioEditCredits) — nicht über die fixen
  // EDIT_KIND_CAPABILITIES (die jetzt nur die Legacy-Kinds führen).
  if (editKind === "studio") return null;
  try {
    const ctx = makePreviewContext(new Date().toISOString());
    const nodes: PipelineNode[] = EDIT_KIND_CAPABILITIES[editKind].map((capId) => {
      const cap = ctx.capabilities.get(capId);
      if (!cap) throw new Error(`Capability '${capId}' nicht registriert`);
      return {
        id: `edit-${capId}`,
        capabilityId: capId,
        capabilityVersion: cap.version,
        surface: "server",
        inputs: {},
      };
    });
    return price({ toolId: EDIT_PRICE_TOOL_ID[editKind], spec: { nodes, outputs: {} } }, PREVIEW_PRICE_BOOK).credits;
  } catch {
    return null;
  }
}

/**
 * D139 — Studio-Edit-Preis-Vorschau: Σ flat-Steps (SPIEGEL von `editPricePlan` studio-Pfad in
 * worker-shared). Jeder Step ist ein flat-Preis-Term unter toolId 'studio' (kein toolBaseFee ⇒
 * Basis 0): timeline-cut(1) + spritesheet-pack(1) = 2 (Schnitt-only-Sheet), timeline-cut(1) = 1
 * (Einzelframe-PNG), +chroma-key(2)+transparency-crop(1) = 5 (Key+Schnitt). NICHT autoritativ —
 * der verbindliche Charge entsteht serverseitig beim Speichern.
 */
export function estimateStudioEditCredits(steps: readonly StudioStep[]): number | null {
  try {
    const nodes: PipelineNode[] = steps.map((step) => ({
      id: `edit-${step}`,
      capabilityId: step,
      capabilityVersion: 1, // flat-Terme: D31-Kette fällt auf den bloßen Book-Key zurück
      surface: "server",
      inputs: {},
    }));
    return price({ toolId: "studio", spec: { nodes, outputs: {} } }, PREVIEW_PRICE_BOOK).credits;
  } catch {
    return null;
  }
}

// D76 — Disabled-Grund sichtbar machen: läuft GENAU denselben Build wie `estimateCredits`
// (getCore → boundSettingsSchema/settingsSchema.parse → buildPipeline → price, gleicher ctx und
// gleiche Reihenfolge), liefert aber den GRUND, warum `estimateCredits` null wurde. Unterschieden
// werden zwei Fälle, weil sie sich völlig anders anfühlen sollen:
//   • `validation` — die SETTINGS verletzen das Zod-Schema (fehlendes/leeres Pflichtfeld wie ein
//     noch leerer Prompt). Das ist KEIN Fehler, nur „noch nicht fertig ausgefüllt" → die UI zeigt
//     einen NEUTRALEN Hinweis (kein roter Alarm, vor allem KEIN roher Zod-JSON-Dump).
//   • `build`      — `buildPipeline`/`price` wirft (echte Inkonsistenz: z.B. zu viele Referenz-
//     bilder für den gepinnten Adapter, ungültige Auflösung) → roter Fehler mit der Klartext-message.
// Reiner Nebenpfad: ändert nichts am verbindlichen Server-Hold (D34).
export type PreviewBuildError = { kind: "validation" | "build"; message: string };

export function previewBuildError(
  toolId: string,
  settings: unknown,
  inputs: AssetRef[] = [],
): PreviewBuildError | null {
  const core = getCore(toolId);
  if (!core) return { kind: "build", message: "Unbekanntes Tool." };
  const ctx = makePreviewContext(new Date().toISOString());
  // D101: rohe Settings mitgeben — exakt derselbe Schema-Pfad wie estimateCredits/Submit.
  const schema = core.boundSettingsSchema ? core.boundSettingsSchema(ctx, settings) : core.settingsSchema;

  // 1) Settings-Schema zuerst (safeParse statt throw) → menschenlesbarer, feldbezogener Hinweis.
  const parsed = schema.safeParse(settings);
  if (!parsed.success) return { kind: "validation", message: describeZodIssues(parsed.error) };

  // 2) Build + Price — wirft hier etwas, ist es eine echte Inkonsistenz (nicht „noch nicht fertig").
  try {
    const spec = core.buildPipeline(parsed.data, inputs, ctx);
    price({ toolId, spec }, PREVIEW_PRICE_BOOK);
    return null;
  } catch (e) {
    return { kind: "build", message: e instanceof Error ? e.message : String(e) };
  }
}

// Zod-Issues → kurze, feldbezogene deutsche Meldung (erste paar, dedupliziert; NIE der rohe
// `ZodError.message`-JSON-Dump). Deckt die in den Tool-Schemata real auftretenden Codes ab.
function describeZodIssues(err: ZodError): string {
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const issue of err.issues) {
    const text = describeZodIssue(issue);
    if (seen.has(text)) continue;
    seen.add(text);
    parts.push(text);
    if (parts.length >= 3) break;
  }
  return parts.length > 0 ? parts.join("; ") : "Eingaben unvollständig";
}

function describeZodIssue(issue: ZodIssue): string {
  const key = issue.path.length > 0 ? String(issue.path[issue.path.length - 1]) : "Eingabe";
  const field = key.charAt(0).toUpperCase() + key.slice(1);
  switch (issue.code) {
    case "too_small":
      return issue.type === "string" && Number(issue.minimum) <= 1
        ? `${field} ist erforderlich`
        : `${field}: Wert zu klein (min. ${issue.minimum})`;
    case "too_big":
      return `${field}: Wert zu groß (max. ${issue.maximum})`;
    case "invalid_type":
      return issue.received === "undefined" ? `${field} ist erforderlich` : `${field}: ungültiger Wert`;
    case "invalid_enum_value":
      return `${field}: ungültige Auswahl`;
    default:
      return `${field}: ${issue.message}`;
  }
}
